import React, { useEffect, useState } from "react";
import { Cookie } from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

const STORAGE_KEY = "norse3_cookie_prefs_v1";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false, performance: true });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) setVisible(true);
      else setPrefs(JSON.parse(saved));
    } catch (e) {
      setVisible(true);
    }
    const open = () => setManageOpen(true);
    window.addEventListener("norse3:open-cookie-settings", open);
    return () => window.removeEventListener("norse3:open-cookie-settings", open);
  }, []);

  const persist = (next) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      /* ignore */
    }
    setPrefs(next);
    window.dispatchEvent(new Event("norse3:consent"));
    setVisible(false);
    setManageOpen(false);
  };

  const acceptAll = () => persist({ analytics: true, marketing: true, performance: true });
  const savePrefs = () => persist(prefs);

  if (!visible && !manageOpen) return null;

  return (
    <>
      {visible && (
        <div data-testid="cookie-banner" className="fixed bottom-4 inset-x-4 z-[60] mx-auto max-w-3xl rounded-xl border border-white/10 bg-navy-800/95 backdrop-blur-xl shadow-card">
          <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-start gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">Cookie preferences</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  We use cookies to understand site usage and improve your
                  experience. Manage your settings or accept all. See our{" "}
                  <a href="/privacy" className="text-primary hover:underline" data-testid="cookie-privacy-link">Privacy Policy</a>.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" className="border-white/15 bg-transparent hover:bg-white/5" onClick={() => setManageOpen(true)} data-testid="cookie-manage-btn">
                Manage
              </Button>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={acceptAll} data-testid="cookie-accept-btn">
                Accept all
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="bg-navy-800 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Control how we collect and use data on this site.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {[
              { key: "analytics", label: "Analytics", desc: "Helps us understand site usage and improve your experience." },
              { key: "marketing", label: "Marketing", desc: "Used to deliver relevant content and messages." },
              { key: "performance", label: "Performance", desc: "Enhances site performance and smooth functionality." },
            ].map((c) => (
              <div key={c.key} className="flex items-start justify-between gap-4 rounded-lg border border-white/10 p-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{c.label}</p>
                  <p className="text-xs text-muted-foreground">{c.desc}</p>
                </div>
                <Switch
                  checked={prefs[c.key]}
                  onCheckedChange={(v) => setPrefs((p) => ({ ...p, [c.key]: v }))}
                />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" className="border-white/15 bg-transparent hover:bg-white/5" onClick={savePrefs} data-testid="cookie-save-btn">
              Save preferences
            </Button>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={acceptAll}>
              Accept all
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
